import type { ErrorADT } from "pb.safe";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { POSConnectorClient } from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";
import { never } from "../../../shared/never";
import * as transport from "../../../transport";
import { createDone } from "../shared/create-done";
import { DeclineResponse_fromRefundPosConnectorResponse } from "../shared/declined-response";
import { getPageProps } from "../shared/get-declined-page-props";
import { isUuid } from "../shared/is-uuid";
import { createOperationCopy } from "../shared/operation-pages";
import {
	AcknowledgedResponse,
	CancelRequest,
	createCloseOnSilence,
	createCloseHandlers,
	createErrorPages,
	createMessageGuard,
	createReconnector,
	createRequestIdGuard,
	type POSConnectorFrame,
	STATE_INITIAL_TIMEOUT,
	STATE_TRANSACTION_STATE_TIMEOUT,
} from "../shared/pos-connector-session";
import { useApiClient } from "../shared/use-api-client";
import { useHealthcheck } from "../shared/use-healthcheck";
import type { RefundMatchedRequest } from "../terminal-refund";
import { getTransaction } from "./refund-matched/get-transaction";
import { getTransactions } from "./refund-matched/get-transactions";
import { Page } from "./refund-matched/page";
import { render, transactionNotFoundCopy } from "./refund-matched/render";
import { getApprovedResponse } from "./shared/get-approved-response";
import {
	RefundAcknowledgedSchema,
	RefundResponseSchema,
	RefundSignatureVerificationSchema,
	RefundTerminalBusySchema,
	RefundTransactionEventSchema,
	type RefundResponse,
} from "./shared/schemas";

const resolve = transport.Resolve<"Terminal_refund">();

/** The pages a silent socket can settle on; `done` needs the `onClose` key. */
type ClosablePage = Extract<Page, { onClose: () => void }>;

type RefundParams = {
	refundAmount: number;
	isoProcessingCode: string;
	transactionUuid: string;
};

export namespace RefundMatched {
	export type Props = {
		request: RefundMatchedRequest;
	};
}

export function RefundMatched(props: RefundMatched.Props) {
	const apiClient = useApiClient();
	const healthcheck = useHealthcheck();

	const { request } = props;

	const [sessionId] = useState(() => request.query.sessionUuid ?? uuidv4());
	// Correlates every frame in one attempt; a retry mints a fresh id so stale
	// frames from the previous attempt are ignored.
	const requestIdRef = useRef(uuidv4());

	const [renderedPage, setRenderedPage] = useState<Page>(() =>
		Page.AwaitTransaction(),
	);
	const setPage = useCallback(
		(nextOrFn: Page | ((prev: Page) => Page)) => {
			// not supported in refunds at present but keep for future support
			if (!request.interactive) {
				return undefined;
			}
			return void setRenderedPage((prev) => {
				const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
				logger.debug("refund: navigation", {
					from: prev.$type,
					to: next.$type,
				});
				return next;
			});
		},
		[request.interactive],
	);

	useEffect(() => {
		const matchesRequestId = createRequestIdGuard(() => requestIdRef.current);

		let _timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		let _connection: POSConnectorClient | undefined = undefined;

		function dispose() {
			transport.onCancel(undefined);
			if (_timeout) _timeout = void clearTimeout(_timeout);
			if (_connection) _connection = void _connection.close();
		}

		function updateTimeout(callback: () => void, delay: number) {
			clearTimeout(_timeout);
			_timeout = setTimeout(callback, delay);
		}

		const done = createDone<Page>({
			dispose,
			setPage,
			interactive: request.interactive,
		});

		// Cancel with no live socket: the pre-flow — locating the transaction,
		// selecting one, amending the amount — has nothing to tell the terminal, so
		// it resolves straight away.
		const onCancelled = () =>
			void done(
				Page.Cancelled({ onClose: closeWithCancelled(copy.Cancelled) }),
				"onClose",
				{
					skip: true,
				},
			);

		transport.onCancel(() => {
			logger.info("refund: matched external cancel called");

			return void onCancelled();
		});

		const _deviceId = request.headers.deviceId;
		if (!_deviceId) {
			return void (() =>
				done(
					Page.ErrorOperation({
						onClose: () =>
							void resolve.Error("Setup Required", {
								message: "Not configured.",
							}),
					}),
					"onClose",
				))();
		}

		const deviceId = _deviceId;
		const {
			amount,
			amountAdjustable,
			purchase,
			reference,
			terminalContact,
			terminalNote,
			terminalReceipt,
			terminalReceiptPrint,
		} = request.query;

		void main();
		async function main() {
			const onRetry = () => void main();
			const pages = createErrorPages<ClosablePage>({
				Page,
				copy,
				close: {
					closeWithConnectionFailure,
					closeWithInactivityTimeout,
					closeWithNetworkFailure,
				},
				onRetry,
			});

			done.isLocked = false;

			/**
			 * `0` is the initial attempt and used when resetting between
			 * reconnection attempts. `5` is last/final attempt.
			 */
			let _hasRequested = false;
			// Survives a reconnect, which re-registers the host cancel handler.
			let _processing = false;
			let _refundParams: RefundParams | undefined = undefined;

			const reconnector = createReconnector({
				deviceId,
				healthcheck,
				updateTimeout,
				connect: () => {
					if (_refundParams) void connect(_refundParams);
				},
				onExhausted: () => void done(pages.disconnected(), "onClose"),
				onNetworkError: () => void done(pages.network(), "onClose"),
			});

			const handle = createMessageGuard({
				onUnreachable: () => void done(pages.terminalUnreachable(), "onClose"),
				onForbidden: () => void done(pages.terminalMissing(), "onClose"),
				onOperationError: () => void done(pages.operation(), "onClose"),
			});

			// Bootstrap auth up front so a missing token fails fast, before any
			// transaction lookup. The token is re-resolved at connect time for a
			// fresh value.
			const authToken = (await request.tokens())?.authAccessToken;
			if (done.isLocked) {
				return;
			}
			if (!authToken) {
				return void done(
					Page.ErrorOperation({
						onClose: () =>
							void resolve.Error("Setup Required", {
								message: "Not authenticated.",
							}),
					}),
					"onClose",
				);
			}

			// Resolve the transaction under the "Locating Transaction" page before
			// opening the POS connector.
			await locate();

			/**
			 * Pre-flow: resolve the original transaction (by UUID, falling back to
			 * external reference) before opening the POS connector. Single exact
			 * match proceeds straight to the refund; otherwise the user picks and/or
			 * adjusts the amount, and their confirmation drives `proceedToRefund`.
			 */
			async function locate() {
				if (!purchase) {
					return void done(
						Page.ErrorTransactionNotFound({
							onClose: closeWithTransactionNotFound,
						}),
						"onClose",
					);
				}

				// When `purchase` is not a valid UUID, the getTransaction call will
				// fail, so skip straight to the reference lookup.
				if (!isUuid(purchase)) {
					return void (await fetchTransactions());
				}

				await fetchTransaction();
			}

			async function fetchTransaction() {
				try {
					const response = await getTransaction({
						apiClient,
						deviceId,
						transactionId: purchase,
					});
					logger.debug("refund: fetching transaction", {
						getTransactionResponse: response,
					});

					if (response instanceof Error) {
						if (!reference) {
							return void transactionError(response);
						}
						return void (await fetchTransactions());
					}

					// if amount passed matches transaction amount proceed to refund
					// automagically
					if (response.amount === amount) {
						return void proceedToRefund({
							refundAmount: response.amount,
							isoProcessingCode: response.isoProcessingCode ?? "",
							transactionUuid: response.id,
						});
					}

					selectRefundAmount({ transaction: response });
				} catch (error) {
					if (error instanceof Error) {
						logger.error("refund: fetch transaction error", {
							errorName: error.name,
							errorMessage: error.message,
							errorStack: error.stack,
						});
					}
					transactionError(new Error("Fetch Transactions Error"));
				}
			}

			async function fetchTransactions() {
				try {
					const response = await getTransactions({
						apiClient,
						deviceId,
						purchaseId: purchase,
					});
					logger.debug("refund: fetching transactions", {
						getTransactionsResponse: response,
					});

					showSelection();

					function showSelection() {
						if (response instanceof Error || !response.transactions.length) {
							return void transactionError(response);
						}

						const [firstTransaction] = response.transactions;

						if (firstTransaction && response.transactions.length === 1) {
							// if amount passed matches transaction amount proceed to refund
							// automagically
							if (firstTransaction.amount === amount) {
								return void proceedToRefund({
									refundAmount: firstTransaction.amount,
									isoProcessingCode: firstTransaction.isoProcessingCode ?? "",
									transactionUuid: firstTransaction.id,
								});
							}

							return void selectRefundAmount({ transaction: firstTransaction });
						}

						setPage(
							Page.SelectTransaction({
								amount,
								amountAdjustable,
								onCancel: onCancelled,
								onConfirm: (transactionId) => {
									const transaction = response.transactions.find(
										(txn) => txn.id === transactionId,
									);

									if (transaction) {
										selectRefundAmount({
											transaction,
											onCancel: showSelection,
											isExternalMatch: true,
										});
									}
								},
								transactions: response.transactions,
							}),
						);
					}
				} catch (error) {
					if (error instanceof Error) {
						logger.error("refund: fetch transactions error", {
							errorName: error.name,
							errorMessage: error.message,
							errorStack: error.stack,
						});
					}
					transactionError(new Error("Fetch Transactions Error"));
				}
			}

			function selectRefundAmount({
				transaction,
				onCancel = onCancelled,
				isExternalMatch = false,
			}: {
				transaction: Exclude<Awaited<ReturnType<typeof getTransaction>>, Error>;
				onCancel?: () => void;
				isExternalMatch?: boolean;
			}) {
				const transactionUuid = transaction.id;
				const isoProcessingCode = transaction.isoProcessingCode ?? "";

				if (!amountAdjustable) {
					return void proceedToRefund({
						refundAmount: amount,
						isoProcessingCode,
						transactionUuid,
					});
				}

				setPage(
					Page.RefundTransaction({
						onCancel,
						onRefund: (refundAmount: number) =>
							proceedToRefund({
								refundAmount,
								isoProcessingCode,
								transactionUuid,
							}),
						refundAmount: amount ?? 0,
						transaction,
						isExternalMatch,
					}),
				);
			}

			function transactionError(
				response:
					| Awaited<ReturnType<typeof getTransactions>>
					| ErrorADT<"NotFound">
					| Error,
			) {
				if (
					(response instanceof Error &&
						"type" in response &&
						response.type === "NotFound") ||
					(!(response instanceof Error) && response.transactions.length === 0)
				) {
					return void done(
						Page.ErrorTransactionNotFound({
							onClose: closeWithTransactionNotFound,
						}),
						"onClose",
					);
				}

				return void done(pages.network(), "onClose");
			}

			function proceedToRefund(params: RefundParams) {
				_refundParams = params;
				_hasRequested = false;
				_processing = false;
				reconnector.reset();
				done.isLocked = false;
				// New attempt (initial or retry) → new requestId; reconnections
				// within an attempt keep the same id.
				requestIdRef.current = uuidv4();
				setPage(Page.Initialising());
				void connect(params);
			}

			function retryRefund() {
				if (_refundParams) {
					proceedToRefund(_refundParams);
				}
			}

			async function connect(refundParams: RefundParams) {
				logger.debug("refund: matched connect", {
					attempt: reconnector.attempt,
				});

				// Nothing is known about the terminal until the socket opens, so this
				// resolves Network Failure, as purchase and unmatched refund do.
				updateTimeout(() => {
					return void done(pages.network(), "onClose");
				}, STATE_INITIAL_TIMEOUT);

				const authToken = (await request.tokens())?.authAccessToken;

				// Prevent continuation if token resolve _after_ timeout.
				if (done.isLocked) {
					return;
				}

				if (!authToken) {
					return void (() =>
						done(
							Page.ErrorOperation({
								onClose: () =>
									void resolve.Error("Setup Required", {
										message: "Not authenticated.",
									}),
							}),
							"onClose",
						))();
				}

				function onExternalCancel() {
					logger.info("refund: matched external cancel called");

					// Only a live socket gets a CANCEL frame; otherwise resolve as the
					// pre-flow does.
					if (!connection.ready) {
						return void onCancelled();
					}

					return void cancel(true);
				}

				transport.onCancel(
					_processing ? transport.onCancel.ignoreExternal : onExternalCancel,
				);

				const connection = POSConnectorClient({
					authToken,
					deviceUuid: deviceId,
					onOpen,
					onClose: reconnector.onClose,
					onError: reconnector.onError,
				});
				_connection = connection;

				function onOpen() {
					logger.debug("refund: matched open");

					if (!_hasRequested) {
						begin();
					} else {
						listen();
					}
				}

				const closeOnSilence = createCloseOnSilence<ClosablePage>({
					connection,
					deviceId,
					healthcheck,
					sessionId,
					done,
					isLocked,
					timer: () => _timeout,
					network: pages.network,
					disconnected: pages.disconnected,
				});

				function isLocked() {
					return done.isLocked;
				}

				// Silence before the terminal acknowledges is never the merchant
				// idling — they have not been shown a prompt yet. A live socket
				// means the terminal itself never picked the request up. Retries
				// resume the refund rather than the whole flow.
				function onSilentRequest() {
					void closeOnSilence(
						() => pages.terminalUnreachable(retryRefund),
						retryRefund,
					);
				}

				function onSilentTransaction() {
					void closeOnSilence(pages.inactivity, onRetry);
				}

				function inactivity() {
					updateTimeout(onSilentTransaction, STATE_TRANSACTION_STATE_TIMEOUT);
				}

				function begin() {
					updateTimeout(onSilentRequest, STATE_INITIAL_TIMEOUT);

					connection.onMessage(
						handle((message) => {
							{
								// Deprecated in favour of `RefundResponseSchema`'s
								// `data.responseCode` of "Q9".
								const event = RefundTerminalBusySchema.safeParse(message).data;
								if (event) {
									return void done(
										Page.ErrorTerminalBusy({
											onClose: closeWithPairedDeviceBusy(
												copy.ErrorTerminalBusy,
											),
											onRetry: retryRefund,
										}),
										"onClose",
									);
								}
							}
							{
								const event = RefundAcknowledgedSchema.safeParse(message).data;
								if (event) {
									if (
										!matchesRequestId(event.requestId, {
											sessionUuid: event.data.sessionUuid,
										})
									) {
										return;
									}

									logger.debug("refund: acknowledge", {
										state: event.data.state,
									});
									// The terminal has the refund; AWAITING_PAYMENT_METHOD
									// moves off this page.
									setPage(Page.InitialisingRefund());
									return void listen();
								}
							}
							// The terminal's opening ACKNOWLEDGED can be lost when its socket
							// rotates. Any frame carrying this attempt's requestId proves the
							// session is live, so open it and replay the frame.
							if (
								message.requestId &&
								matchesRequestId(message.requestId, { action: message.action })
							) {
								logger.warn("refund: matched frame before acknowledge");
								setPage(Page.InitialisingRefund());
								return void listen()(message);
							}
						}),
					);

					sendRefundRequest({
						amount: refundParams.refundAmount,
						client: connection,
						isoProcessingCode: refundParams.isoProcessingCode,
						purchase: refundParams.transactionUuid,
						reference: reference ?? "",
						requestId: requestIdRef.current,
						sessionUuid: sessionId,
						terminalContact,
						terminalNote,
						terminalReceipt,
						terminalReceiptPrint,
					});
					_hasRequested = true;
				}

				function listen() {
					inactivity();

					function next(page: Page) {
						// The terminal can't honour a cancel while it is processing, so drop
						// the host's request; the screen has no close control here either.
						_processing = page.$type === "AwaitProcessing";
						transport.onCancel(
							_processing
								? transport.onCancel.ignoreExternal
								: onExternalCancel,
						);
						setPage(page);
						// Reset reconnection attempts when we successfully receive data.
						reconnector.reset();
						// Reset inactivity timeout on every navigation.
						inactivity();
					}

					const onFrame = (message: POSConnectorFrame) => {
						{
							// Deprecated in favour of `RefundResponseSchema`'s
							// `data.responseCode` of "Q9".
							const event = RefundTerminalBusySchema.safeParse(message).data;
							if (event) {
								return void done(
									Page.ErrorTerminalBusy({
										onClose: closeWithPairedDeviceBusy(copy.ErrorTerminalBusy),
										onRetry: retryRefund,
									}),
									"onClose",
								);
							}
						}
						{
							const event = RefundResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									if (
										!matchesRequestId(event.requestId, {
											status: event.data.status,
										})
									) {
										return;
									}

									connection.send(
										AcknowledgedResponse({
											action: "refund",
											sessionId,
											requestId: requestIdRef.current,
										}),
									);

									if (event.data.status === "APPROVED") {
										return void approved(event);
									}
									return void declined(event);
								})();
							}
						}
						{
							const event =
								RefundTransactionEventSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									resolve.Event(event.data.event, {
										message: event.data.displayMessage,
									});

									switch (event.data.event) {
										case "AWAITING_ACCOUNT_SELECTION":
											return next(
												Page.AwaitAccountSelection({ onClose: cancel }),
											);
										case "AWAITING_APPLICATION_SELECTION":
											return next(
												Page.AwaitApplicationSelection({
													onClose: cancel,
												}),
											);
										case "AWAITING_PAYMENT_METHOD":
											return next(Page.AwaitPaymentMethod({ onClose: cancel }));
										case "AWAITING_PIN_ENTRY":
											return next(Page.AwaitPinEntry({ onClose: cancel }));
										case "PROCESSING":
											return next(Page.AwaitProcessing({ ...refundParams }));
										case "DISPLAYING_PAYMENT_MESSAGE":
										case "RETRYING_PAYMENT_METHOD":
											return next(
												Page.DisplayPaymentMessage({ onClose: cancel }),
											);
										default:
											logger.debug(
												"refund: matched transaction event unhandled variant",
											);
											return never(event.data.event);
									}
								})();
							}
						}
						{
							const event =
								RefundSignatureVerificationSchema.safeParse(message)?.data;
							if (event) {
								return void (() => {
									if (!event.requestId) {
										logger.warn("refund: signature missing requestId", {
											state: event.data.state,
										});
									}

									resolve.Event(event.data.state, {
										message: event.data.signature,
									});

									switch (event.data.state) {
										case "AWAITING_SIGNATURE":
										case "SIGNATURE_CLEARED":
											return next(Page.AwaitSignature({ onClose: cancel }));
										case "SIGNATURE_SUBMITTED":
											return next(
												Page.SignatureSubmitted({
													signature: event.data.signature,
													onClose: closeWithCancelled(copy.Cancelled),
													onApprove: () =>
														sendSignatureVerificationEvent(
															connection,
															sessionId,
															true,
															event.requestId,
														),
													onDecline: () =>
														sendSignatureVerificationEvent(
															connection,
															sessionId,
															false,
															event.requestId,
														),
												}),
											);
										default:
											logger.debug(
												"refund: matched signature verification unhandled variant",
											);
											return never(event.data.state);
									}
								})();
							}
						}
					};
					connection.onMessage(handle(onFrame));
					return onFrame;
				}

				function approved(event: RefundResponse) {
					const { response, getPageProps } = getApprovedResponse({
						event: event.data,
						reference: reference ?? "",
					});
					done(
						Page.Approved({
							...getPageProps(),
							onDone: () => void resolve(response),
						}),
						"onDone",
					);
					// Response screens ignore the host cancel; done() otherwise restores
					// the default "Cancelled" resolution.
					transport.onCancel(transport.onCancel.ignoreExternal);
				}

				function declined(event: RefundResponse) {
					const response = DeclineResponse_fromRefundPosConnectorResponse({
						data: event.data,
						reference,
					});
					done(
						Page.Declined({
							...getPageProps({ data: event.data }),
							onCancel: () => void resolve(response),
							onRetry: retryRefund,
						}),
						"onCancel",
					);
					transport.onCancel(transport.onCancel.ignoreExternal);
				}

				function cancel(external = false) {
					/* v8 ignore start -- UI close handlers forward the click event, so `external` is always truthy here; the falsy branch is only reachable via a direct zero-arg call */
					if (!external) {
						logger.info("refund: matched internal cancel called");
					}
					/* v8 ignore stop */

					setPage((prev) => {
						return { ...prev, cancelling: true };
					});

					connection.onMessage(
						handle((message) => {
							const event = RefundResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									if (
										!matchesRequestId(event.requestId, {
											status: event.data.status,
										})
									) {
										return;
									}

									connection.send(
										AcknowledgedResponse({
											action: "refund",
											sessionId,
											requestId: requestIdRef.current,
										}),
									);

									if (event.data.status === "APPROVED") {
										return approved(event);
									}

									const response =
										DeclineResponse_fromRefundPosConnectorResponse({
											data: event.data,
											reference,
										});
									return void done(
										Page.Cancelled({
											onClose: () => void resolve(response),
										}),
										"onClose",
										{ skip: true },
									);
								})();
							}
						}),
					);

					connection.send(CancelRequest(sessionId));
					updateTimeout(() => {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled(copy.Cancelled) }),
							"onClose",
						);
					}, STATE_TRANSACTION_STATE_TIMEOUT);
				}
			}
		}

		return () => {
			// A silence classification in flight must not terminate an unmounted flow.
			done.isLocked = true;
			dispose();
		};
	}, [apiClient, healthcheck, request, sessionId, setPage]);

	logger.debug("refund: matched render", { state: renderedPage });
	return render(renderedPage);
}

function sendRefundRequest({
	amount,
	client,
	isoProcessingCode,
	purchase,
	reference,
	requestId,
	sessionUuid,
	terminalContact,
	terminalNote,
	terminalReceipt,
	terminalReceiptPrint,
}: {
	amount: number;
	client: POSConnectorClient;
	isoProcessingCode: string;
	purchase: string;
	reference: string;
	requestId: string;
	sessionUuid: string;
	terminalContact?: boolean;
	terminalNote?: boolean;
	terminalReceipt?: boolean;
	terminalReceiptPrint?: boolean;
}) {
	client.send({
		eventId: uuidv4(),
		action: "refund",
		requestId,
		timestamp: Date.now().toString(),
		data: {
			amount,
			externalReference: reference,
			originalIsoProcessingCode: isoProcessingCode,
			originalTransactionUuid: purchase,
			sessionUuid,
			terminalContact,
			terminalNote,
			terminalReceipt,
			terminalReceiptPrint,
			transactionUuid: uuidv4(),
		},
	});
}

function sendSignatureVerificationEvent(
	client: POSConnectorClient,
	sessionUuid: string,
	isValid: boolean,
	signatureRequestId: string | undefined,
) {
	client.send({
		action: "signatureVerification",
		eventId: uuidv4(),
		requestId: signatureRequestId,
		timestamp: Date.now().toString(),
		data: {
			event: "SIGNATURE_VERIFICATION",
			isValid,
			sessionUuid,
		},
	});
}

const copy = createOperationCopy("Refund");
const {
	closeWithCancelled,
	closeWithConnectionFailure,
	closeWithInactivityTimeout,
	closeWithNetworkFailure,
	closeWithPairedDeviceBusy,
} = createCloseHandlers({ resolveError: resolve.Error });

function closeWithTransactionNotFound() {
	logger.warn("refund: matched closing with transaction not found");
	return void resolve.Error("Transaction Not Found", {
		message: transactionNotFoundCopy.message.join(" "),
	});
}
